import './ResultsSection.css';
import React, { useState, useEffect, useCallback } from 'react';
import { loadResults, loadResultAnswers, statusBadgeClass } from '../../logic/resultsLogic';

const IMG_BASE = process.env.REACT_APP_IMG_BASE;

export default function ResultsSection({ notify, can }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewAnswers, setPreviewAnswers] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadResults();
      setResults(data);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load results', true); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const openAnswerPreview = async (result) => {
    setPreviewResult(result);
    setPreviewAnswers([]);
    setPreviewLoading(true);
    try {
      const answers = await loadResultAnswers(result.id);
      setPreviewAnswers(answers);
    } catch (e) {
      notify(e.response?.data?.message || 'Failed to load answers', true);
      setPreviewResult(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="fade-up">
      <div className="results-actions">
        <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>{loading?'↻ …':'↻ Refresh'}</button>
        <span className="results-count">{results.length} records</span>
      </div>
      {loading ? <div className="admin-loading"><div className="spinner"/></div>
      : results.length===0 ? <div className="empty-state"><span>📭</span><p>No results yet.</p></div>
      : <div className="results-table-wrap"><table className="results-table">
          <thead><tr><th>Candidate</th><th>Score</th><th>✓</th><th>✗</th><th>–</th><th>Status</th><th>Violations</th><th>Submitted</th><th>Preview</th></tr></thead>
          <tbody>{results.map(r=>(
            <tr key={r.id}>
              <td><div className="candidate-cell">
                <div className="candidate-avatar">{(r.full_name||r.username||'?')[0].toUpperCase()}</div>
                <div>
                  <div className="cname">{r.full_name||r.username}</div>
                  <div className="cemail">{r.email}</div>
                  {r.phone    && <div className="cdept">📞 {r.phone}</div>}
                  {r.department && <div className="cdept">{r.department}</div>}
                </div>
              </div></td>
              <td><div className={`score-cell ${r.passed?'passed':'failed'}`}>
                <span className="score-pct">{r.percentage}%</span>
                <span className={`badge ${r.passed?'badge-green':'badge-red'}`}>{r.passed?'PASS':'FAIL'}</span>
              </div></td>
              <td><span style={{color:'var(--green)',fontFamily:'DM Mono,monospace'}}>{r.correct}</span></td>
              <td><span style={{color:'var(--red)',  fontFamily:'DM Mono,monospace'}}>{r.incorrect}</span></td>
              <td><span style={{color:'var(--muted)',fontFamily:'DM Mono,monospace'}}>{r.skipped}</span></td>
              <td><span className={`badge ${statusBadgeClass(r.status)}`}>{r.status?.replace(/_/g,' ')}</span></td>
              <td><span className={`badge ${r.violation_count>0?'badge-gold':'badge-green'}`}>{r.violation_count}</span></td>
              <td className="date-cell">{r.submitted_at?new Date(r.submitted_at).toLocaleString():'—'}</td>
              <td>
                <button className="act-btn info" title="Preview answers" onClick={()=>openAnswerPreview(r)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>}

      {/* ════════ ANSWER PREVIEW MODAL ════════ */}
      {previewResult && (
        <div className="ev-overlay" onClick={()=>setPreviewResult(null)}>
          <div className="ap-modal" onClick={e=>e.stopPropagation()}>
            <div className="ev-modal-header">
              <div className="ev-modal-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color:'var(--accent2)'}}>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                Answer Preview — {previewResult.full_name || previewResult.username}
                <span style={{fontSize:'0.78rem',fontWeight:400,color:'var(--muted)',marginLeft:'0.25rem'}}>({previewResult.email})</span>
              </div>
              <button className="ev-close-btn" onClick={()=>setPreviewResult(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="ap-summary-bar">
              <div className="ap-summary-chip">
                <span style={{color:'var(--text)',fontFamily:'DM Mono,monospace',fontSize:'1.1rem',fontWeight:600}}>{previewResult.percentage}%</span>
                <span className={`badge ${previewResult.passed?'badge-green':'badge-red'}`} style={{marginLeft:'0.4rem'}}>{previewResult.passed?'PASS':'FAIL'}</span>
              </div>
              <div className="ap-summary-chip"><span style={{color:'var(--green)',fontFamily:'DM Mono,monospace'}}>✓ {previewResult.correct}</span><span>Correct</span></div>
              <div className="ap-summary-chip"><span style={{color:'var(--red)',fontFamily:'DM Mono,monospace'}}>✗ {previewResult.incorrect}</span><span>Wrong</span></div>
              <div className="ap-summary-chip"><span style={{color:'var(--muted)',fontFamily:'DM Mono,monospace'}}>– {previewResult.skipped}</span><span>Skipped</span></div>
              <div className="ap-summary-chip"><span style={{fontFamily:'DM Mono,monospace'}}>{previewResult.scored_marks}/{previewResult.total_marks}</span><span>Marks</span></div>
            </div>
            <div className="ap-modal-body">
              {previewLoading ? (
                <div className="admin-loading" style={{height:'200px'}}><div className="spinner"/></div>
              ) : previewAnswers.length === 0 ? (
                <div className="empty-state"><span>📭</span><p>No question data available.</p></div>
              ) : (
                <div className="ap-questions-list">
                  {previewAnswers.map(q => {
                    const skipped   = q.selected_option === null;
                    const correct   = !skipped && q.selected_option === q.correct_option;
                    const statusCls = skipped ? 'ap-q-skipped' : correct ? 'ap-q-correct' : 'ap-q-wrong';
                    const optLabels = { A: q.option_a, B: q.option_b, C: q.option_c, D: q.option_d };
                    const optImages = { A: q.option_a_image, B: q.option_b_image, C: q.option_c_image, D: q.option_d_image };
                    return (
                      <div key={q.question_id} className={`ap-question-card ${statusCls}`}>
                        <div className="ap-q-header">
                          <span className="ap-q-num">Q{q.index}</span>
                          {q.category && <span className="badge badge-purple" style={{fontSize:'0.7rem'}}>{q.category}</span>}
                          <span className={`badge ${q.difficulty==='easy'?'badge-green':q.difficulty==='hard'?'badge-red':'badge-gold'}`} style={{fontSize:'0.7rem'}}>{q.difficulty}</span>
                          <span style={{marginLeft:'auto',fontSize:'0.72rem',color:'var(--muted)',fontFamily:'DM Mono,monospace'}}>{q.marks} mk</span>
                          <span className={`ap-status-badge ${statusCls}`}>
                            {skipped ? '— Skipped' : correct ? '✓ Correct' : '✗ Wrong'}
                          </span>
                        </div>
                        <p className="ap-q-text">{q.question_text}</p>
                        {q.question_image && (
                          <div className="ap-q-img-wrap"><img src={`${IMG_BASE}${q.question_image}`} alt="Question" className="ap-q-img"/></div>
                        )}
                        <div className="ap-options-grid">
                          {['A','B','C','D'].map(opt => {
                            const isCorrect  = opt === q.correct_option;
                            const isSelected = opt === q.selected_option;
                            let cls = 'ap-option';
                            if (isCorrect && isSelected)  cls += ' ap-opt-correct-selected';
                            else if (isCorrect)            cls += ' ap-opt-correct';
                            else if (isSelected)           cls += ' ap-opt-wrong-selected';
                            return (
                              <div key={opt} className={cls}>
                                <span className="ap-opt-label">{opt}</span>
                                <div className="ap-opt-body">
                                  <span className="ap-opt-text">{optLabels[opt]}</span>
                                  {optImages[opt] && <img src={`${IMG_BASE}${optImages[opt]}`} alt={`Option ${opt}`} className="ap-opt-img"/>}
                                </div>
                                {isSelected && !isCorrect && <span className="ap-opt-tag ap-tag-wrong">Your answer</span>}
                                {isCorrect && <span className="ap-opt-tag ap-tag-correct">Correct</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="ev-modal-footer" style={{justifyContent:'flex-end'}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setPreviewResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
